import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, User, Phone, LogIn, UserPlus } from "lucide-react";
import Swal from "sweetalert2";
import api from "../utils/api";

export default function InlineAuth() {
  const { user, login, register: registerAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Redirect toward the dashboard or stored 'from' location
    if (user && (location.pathname === "/login" || location.pathname === "/register")) {
      const from = location.state?.from || "/dashboard";
      navigate(from);
    }
  }, [user, navigate, location.pathname, location.state]);

  const [isLogin, setIsLogin] = useState(true);
  const [isForgot, setIsForgot] = useState(false);
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    newPassword: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      // Logic for login
      if (!form.email || !form.password) {
        return Swal.fire({ icon: 'error', title: 'Error', text: 'Email and password are required', background: '#0b0b0b', color: '#fff' });
      }
      await login(form.email, form.password);
    } else {
      // Logic for register
      if (!form.name || !form.email || !form.password || !form.phone) {
        return Swal.fire({ icon: 'error', title: 'Error', text: 'All fields are required', background: '#0b0b0b', color: '#fff' });
      }
      const success = await registerAuth(form.name, form.email, form.password, form.phone);
      if (success) setIsLogin(true); // switch to login after registering
    }
  };

  const handleVerifyUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/auth/forgot-password", { email: form.email, phone: form.phone });
      Swal.fire({ icon: 'success', title: 'User Verified', background: '#0b0b0b', color: '#fff' });
      setStep(2);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'User Not Found', background: '#0b0b0b', color: '#fff' });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/auth/reset-password", { email: form.email, phone: form.phone, newPassword: form.newPassword });
      Swal.fire({ icon: 'success', title: 'Password Updated', background: '#0b0b0b', color: '#fff' });
      setIsForgot(false);
      setStep(1);
      setIsLogin(true);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Reset Failed', background: '#0b0b0b', color: '#fff' });
    }
  };

  return (
    <div className="auth-box" style={{ margin: "10px auto" }}>
      <h2>
        {isForgot
          ? step === 1 ? "Verify Account" : "Reset Password"
          : isLogin ? "Login" : "Register"}
      </h2>
      <p style={{ marginBottom: "10px", color: "var(--text-dim)" }}>
        {isForgot ? "Recover your account credentials" : "To proceed with your booking"}
      </p>

      {/* 🔐 FORGOT PASSWORD */}
      {isForgot ? (
        <form onSubmit={step === 1 ? handleVerifyUser : handleResetPassword}>
          {step === 1 ? (
            <>
              <div className="input-field">
                <Mail size={18} />
                <input 
                  type="email" 
                  placeholder="Email" 
                  onChange={(e) => setForm({ ...form, email: e.target.value })} 
                  required 
                />
              </div>
              <div className="input-field">
                <Phone size={18} />
                <input 
                  type="text" 
                  placeholder="Phone" 
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                  required 
                />
              </div>
              <button type="submit" className="auth-btn">Verify Account</button>
            </>
          ) : (
            <>
              <div className="input-field">
                <Lock size={18} />
                <input 
                  type="password" 
                  placeholder="New Password" 
                  onChange={(e) => setForm({ ...form, newPassword: e.target.value })} 
                  required 
                />
              </div>
              <button type="submit" className="auth-btn">Reset Password</button>
            </>
          )}
          <p className="auth-footer" style={{ cursor: "pointer" }} onClick={() => setIsForgot(false)}>
            Back to Login
          </p>
        </form>
      ) : (
        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="input-field">
              <User size={18} />
              <input 
                type="text" 
                placeholder="Full Name" 
                onChange={(e) => setForm({ ...form, name: e.target.value })} 
                required 
              />
            </div>
          )}

          <div className="input-field">
            <Mail size={18} />
            <input 
              type="email" 
              placeholder="Email Address" 
              onChange={(e) => setForm({ ...form, email: e.target.value })} 
              required 
            />
          </div>

          {!isLogin && (
            <div className="input-field">
              <Phone size={18} />
              <input 
                type="text" 
                placeholder="Phone Number" 
                onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                required 
              />
            </div>
          )}

          <div className="input-field">
            <Lock size={18} />
            <input 
              type="password" 
              placeholder="Password" 
              onChange={(e) => setForm({ ...form, password: e.target.value })} 
              required 
            />
          </div>

          <button type="submit" className="auth-btn">
            {isLogin ? <><LogIn size={18} /> Login</> : <><UserPlus size={18} /> Register</>}
          </button>

          {isLogin && (
            <p className="auth-footer" style={{ cursor: "pointer", color: "#c9a646", marginTop: "5px" }} onClick={() => setIsForgot(true)}>
              Forgot Password?
            </p>
          )}

          <p className="auth-footer" style={{ cursor: "pointer" }} onClick={() => setIsLogin(!isLogin)}>
            {isLogin ? "Don't have an account? Register Now" : "Already have an account? Login"}
          </p>
        </form>
      )}
    </div>
  );
}
